import type { ProjectStatus } from '@tms/shared';
import { Link, useNavigate } from '@tanstack/react-router';
import { Archive, FolderKanban, Plus, RotateCcw, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { PageHeader } from '../../components/layout/PageHeader';
import {
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  EntityKey,
  ErrorState,
  Input,
  Menu,
  MenuItem,
  Pagination,
  SelectMenu,
  SelectMenuItem,
  SkeletonRows,
  Table,
  TableWrap,
  Td,
  Th,
  Tr,
} from '../../components/ui';
import { useEnumLabels, useErrorText, useFormat, useT } from '../../i18n';
import { useDebounced } from '../../lib/use-debounced';
import { usePermissions } from '../auth/permissions';
import { ProjectFormDialog } from './ProjectFormDialog';
import { ProjectIcon } from './ProjectIcon';
import { useDeleteProject, useProjects, useSetProjectStatus } from './api';

/** Sentinel for the unfiltered state: a select value is always a string. */
const ANY_STATUS = 'any';

export const ProjectsPage = () => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { t } = useT();
  const { relative } = useFormat();
  const { projectStatus } = useEnumLabels();
  const { errorText } = useErrorText();

  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<ProjectStatus | ''>('');
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<{ code: string; name: string } | null>(null);

  const debouncedSearch = useDebounced(search);
  const { data, isPending, error, refetch } = useProjects({
    page,
    ...(status ? { status } : {}),
    ...(debouncedSearch ? { q: debouncedSearch } : {}),
  });

  const deleteProject = useDeleteProject();

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deleteProject.mutateAsync(pendingDelete.code);
      toast.success(t('projects.deleted', { code: pendingDelete.code }));
      setPendingDelete(null);
    } catch (deleteError) {
      toast.error(errorText(deleteError, 'projects.deleteFailed'));
    }
  };

  return (
    <>
      <PageHeader
        title={t('projects.title')}
        description={t('projects.subtitle')}
        actions={
          can('project:write') ? (
            <Button variant="primary" onClick={() => setCreateOpen(true)}>
              <Plus className="size-4" />
              {t('projects.new')}
            </Button>
          ) : null
        }
      />

      <Card>
        <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2.5">
          <Input
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            placeholder={t('projects.searchPlaceholder')}
            className="w-full sm:w-64"
            aria-label={t('projects.searchLabel')}
          />
          <SelectMenu
            value={status === '' ? ANY_STATUS : status}
            onValueChange={(value) => {
              setStatus(value === ANY_STATUS ? '' : (value as ProjectStatus));
              setPage(1);
            }}
            className="w-40"
            aria-label={t('projects.filterStatus')}
          >
            <SelectMenuItem value={ANY_STATUS}>{t('projects.allStatuses')}</SelectMenuItem>
            <SelectMenuItem value="active">{projectStatus('active')}</SelectMenuItem>
            <SelectMenuItem value="archived">{projectStatus('archived')}</SelectMenuItem>
          </SelectMenu>
        </div>

        {error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : isPending ? (
          <SkeletonRows />
        ) : data.data.length === 0 ? (
          <EmptyState
            icon={<FolderKanban className="size-6" />}
            title={
              debouncedSearch || status ? t('projects.emptyFiltered') : t('projects.empty')
            }
            description={
              debouncedSearch || status
                ? t('projects.emptyFilteredHint')
                : t('projects.emptyHint')
            }
            action={
              can('project:write') && !debouncedSearch && !status ? (
                <Button variant="primary" onClick={() => setCreateOpen(true)}>
                  <Plus className="size-4" />
                  {t('projects.create')}
                </Button>
              ) : null
            }
          />
        ) : (
          <>
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    {/* The icon reads as part of the project's identity, next
                        to its key, so it carries no heading of its own. */}
                    <Th className="w-12" />
                    <Th className="w-28">{t('projects.columnKey')}</Th>
                    <Th className="w-full">{t('projects.columnProject')}</Th>
                    <Th className="text-right">{t('projects.columnCases')}</Th>
                    <Th className="text-right">{t('projects.columnChecklists')}</Th>
                    <Th className="text-right">{t('projects.columnRuns')}</Th>
                    <Th>{t('common.status')}</Th>
                    <Th>{t('projects.columnLastActivity')}</Th>
                    <Th className="w-10" />
                  </tr>
                </thead>
                <tbody>
                  {data.data.map((project) => (
                    <Tr
                      key={project.id}
                      onClick={() =>
                        void navigate({
                          to: '/projects/$projectCode/overview',
                          params: { projectCode: project.code },
                        })
                      }
                    >
                      <Td>
                        <ProjectIcon icon={project.icon} color={project.iconColor} />
                      </Td>
                      <Td>
                        <EntityKey className="rounded bg-sunken px-1.5 py-0.5 text-ink-muted">
                          {project.code}
                        </EntityKey>
                      </Td>
                      <Td>
                        <Link
                          to="/projects/$projectCode/overview"
                          params={{ projectCode: project.code }}
                          className="font-medium text-ink hover:text-accent-fg"
                          onClick={(event) => event.stopPropagation()}
                        >
                          {project.name}
                        </Link>
                        {project.description ? (
                          <p className="mt-0.5 line-clamp-1 max-w-md text-[12px] text-ink-subtle">
                            {project.description}
                          </p>
                        ) : null}
                      </Td>
                      {/* The counters and the two columns after them are read
                          across, not down: none of them may wrap. */}
                      <Td className="whitespace-nowrap text-right tabular-nums">
                        {project.counts.testCases}
                      </Td>
                      <Td className="whitespace-nowrap text-right tabular-nums">
                        {project.counts.checklists}
                      </Td>
                      <Td className="whitespace-nowrap text-right tabular-nums">
                        {project.counts.testRuns}
                        {project.counts.activeTestRuns > 0 ? (
                          <span className="ml-1 text-[12px] text-accent-fg">
                            {t('projects.activeRunsSuffix', { count: project.counts.activeTestRuns })}
                          </span>
                        ) : null}
                      </Td>
                      <Td className="whitespace-nowrap">
                        <span
                          className={
                            project.status === 'active'
                              ? 'text-[12px] font-medium text-pass'
                              : 'text-[12px] font-medium text-ink-subtle'
                          }
                        >
                          {projectStatus(project.status)}
                        </span>
                      </Td>
                      <Td className="whitespace-nowrap text-ink-muted">
                        {relative(project.lastActivityAt)}
                      </Td>
                      <Td>
                        <div onClick={(event) => event.stopPropagation()}>
                          <ProjectRowMenu
                            code={project.code}
                            name={project.name}
                            status={project.status}
                            onDelete={() =>
                              setPendingDelete({ code: project.code, name: project.name })
                            }
                          />
                        </div>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
            <Pagination meta={data.meta} onPageChange={setPage} />
          </>
        )}
      </Card>

      <ProjectFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(project) =>
          void navigate({
            to: '/projects/$projectCode/overview',
            params: { projectCode: project.code },
          })
        }
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title={t('projects.deleteTitle', { name: pendingDelete?.name ?? '' })}
        description={t('projects.deleteHint')}
        confirmLabel={t('projects.deleteAction')}
        loading={deleteProject.isPending}
        onConfirm={() => void confirmDelete()}
      />
    </>
  );
};

const ProjectRowMenu = ({
  code,
  status,
  onDelete,
}: {
  code: string;
  name: string;
  status: ProjectStatus;
  onDelete: () => void;
}) => {
  const { t } = useT();
  const { errorText } = useErrorText();
  const { can } = usePermissions();
  const setStatus = useSetProjectStatus(code);

  if (!can('project:write')) return null;

  const toggleStatus = async () => {
    try {
      await setStatus.mutateAsync(status === 'active' ? 'archive' : 'restore');
      toast.success(
        status === 'active'
          ? t('projects.archived', { code })
          : t('projects.restored', { code }),
      );
    } catch (error) {
      toast.error(errorText(error, 'projects.statusChangeFailed'));
    }
  };

  return (
    <Menu>
      <MenuItem onSelect={() => void toggleStatus()}>
        {status === 'active' ? (
          <>
            <Archive className="size-3.5" />
            {t('projects.archive')}
          </>
        ) : (
          <>
            <RotateCcw className="size-3.5" />
            {t('projects.restore')}
          </>
        )}
      </MenuItem>
      {can('project:delete') ? (
        <MenuItem destructive onSelect={onDelete}>
          <Trash2 className="size-3.5" />
          {t('projects.deleteAction')}
        </MenuItem>
      ) : null}
    </Menu>
  );
};
